import { Injectable, inject, signal } from '@angular/core';

import { SuiToastService } from '@semeris/ui';
import { SheetApi } from '@semeris/workspace-ui';
import type { ActionBlock } from '@semeris/workspace-ui/chat-config';
import { Observable } from 'rxjs';

import { ChatActionEvent } from '../models/chat-document-ref.model';
import { SdocsChartApi } from '../transport/sdocs-chart.api';

/**
 * Artifact kinds the assistant produces inline, distinct from cited documents.
 *
 * `sheet` is the SQL-grid artifact `create_sheet` emits — not the assistant's separate
 * `create_report` artifact, which is a scheduled document recipe sdocs does not surface.
 */
export type ChatArtifactType = 'chart' | 'sheet';

/** What the user asked for by clicking an artifact-card button. */
export type ChatArtifactIntent =
  | { kind: 'save'; type: ChatArtifactType; id: string }
  | { kind: 'open'; type: ChatArtifactType; id: string; label: string };

const CHART_OPEN_ACTIONS = new Set(['open_chart', 'preview_chart', 'goto_chart', 'view_chart']);
const SHEET_OPEN_ACTIONS = new Set(['open_sheet', 'preview_sheet', 'goto_sheet', 'view_sheet']);

const DEFAULT_LABELS: Record<ChatArtifactType, string> = { chart: 'Chart', sheet: 'Sheet' };

function artifactIdOf(block: ActionBlock): string | undefined {
  return block.payload.chartId || block.payload.sheetId || undefined;
}

/**
 * Shared handling of chart/sheet artifact-card actions, for every `<zing-chat>` host.
 *
 * Owns the action-name vocabulary so hosts can't drift on which strings count as "open".
 * Saving is host-independent and performed here; opening is handed back via {@link classify},
 * since the assistant page has an artifact panel and deal-details has to hand off.
 */
@Injectable({ providedIn: 'root' })
export class ChatArtifactActionsService {
  private readonly chartApi = inject(SdocsChartApi);
  private readonly sheetApi = inject(SheetApi);
  private readonly toast = inject(SuiToastService);

  private readonly savedIds = signal<ReadonlySet<string>>(new Set());
  private readonly savingIds = signal<ReadonlySet<string>>(new Set());

  /**
   * `CHAT_CONFIG` resolver implementations, so an artifact card can drop its Save button once the
   * artifact is persisted and spin only while the write is actually in flight.
   *
   * Artifacts stream in as drafts and carry no persisted flag, so 'saved' is only known from a
   * save this session — a card restored from history shows Save again.
   */
  public readonly chatResolvers = {
    resolveArtifactState: (block: ActionBlock): 'draft' | 'saved' | null => {
      const id = artifactIdOf(block);
      if (!id) {
        return null;
      }
      return this.savedIds().has(id) ? 'saved' : 'draft';
    },
    isArtifactBusy: (block: ActionBlock): boolean => {
      const id = artifactIdOf(block);
      return !!id && this.savingIds().has(id);
    },
  };

  /** The chart/sheet intent behind a chat action, or `undefined` when it isn't one (e.g. a citation). */
  public classify(action: ChatActionEvent): ChatArtifactIntent | undefined {
    const chartId = action.payload.chartId;
    if (chartId) {
      if (action.action === 'save_chart') {
        return { kind: 'save', type: 'chart', id: chartId };
      }
      if (CHART_OPEN_ACTIONS.has(action.action)) {
        return { kind: 'open', type: 'chart', id: chartId, label: this.labelFor(action, 'chart') };
      }
    }

    const sheetId = action.payload.sheetId;
    if (sheetId) {
      if (action.action === 'save_sheet') {
        return { kind: 'save', type: 'sheet', id: sheetId };
      }
      if (SHEET_OPEN_ACTIONS.has(action.action)) {
        return { kind: 'open', type: 'sheet', id: sheetId, label: this.labelFor(action, 'sheet') };
      }
    }

    return undefined;
  }

  /** Persist a draft artifact, reporting the outcome as a toast. */
  public save(intent: Extract<ChatArtifactIntent, { kind: 'save' }>, workspaceId: string | undefined): void {
    if (!workspaceId) {
      this.toast.error('Select a workspace before saving.');
      return;
    }

    // Base Observable: `subscribe` isn't callable on the union of the two branches' types.
    const request$: Observable<unknown> =
      intent.type === 'chart'
        ? this.chartApi.saveChart(workspaceId, intent.id)
        : this.sheetApi.update(workspaceId, intent.id, { draft: false });
    const noun = DEFAULT_LABELS[intent.type];

    this.savingIds.update((ids) => new Set(ids).add(intent.id));
    request$.subscribe({
      next: () => {
        this.savedIds.update((ids) => new Set(ids).add(intent.id));
        this.clearSaving(intent.id);
        this.toast.success(`${noun} saved`);
      },
      error: () => {
        this.clearSaving(intent.id);
        this.toast.error(`Failed to save ${noun.toLowerCase()}`);
      },
    });
  }

  private clearSaving(id: string): void {
    this.savingIds.update((ids) => {
      const next = new Set(ids);
      next.delete(id);
      return next;
    });
  }

  private labelFor(action: ChatActionEvent, type: ChatArtifactType): string {
    return action.payload.title || action.payload.name || DEFAULT_LABELS[type];
  }
}
